import { useTranslation } from 'react-i18next';
import type { ManualBookingPriceSnapshot } from '@/shared/api/types/manualBookingPrice';
import { formatMoney } from '@/shared/lib/format';
import { Button } from '@/shared/components';

export function ManualPriceSummary({
  quote,
  loading,
  error,
  onRetry,
}: {
  quote?: ManualBookingPriceSnapshot;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation('manualBooking');
  if (error)
    return (
      <div role="alert" className="text-sealwax text-sm">
        <p>{t('previewError')}</p>
        <Button variant="ghost" className="min-h-11" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    );
  if (loading)
    return (
      <p role="status" className="text-ink-soft text-sm">
        {t('previewLoading')}
      </p>
    );
  if (!quote) return null;
  return (
    <div className="border-hairline rounded-control space-y-2 border p-3 text-sm">
      <p>
        {t('nights', { count: quote.contractNights })} ·{' '}
        {quote.basis === 'TotalStay' ? t('average') : t('perNight')}{' '}
        {formatMoney(quote.accommodationGross / quote.contractNights, quote.currency)}
      </p>
      {quote.lines.map((line) => (
        <div key={line.key} className="flex justify-between gap-3">
          <span>
            {line.label}
            {line.key !== 'accommodation' ? t('additional') : ''}
          </span>
          <span>{formatMoney(line.grossAmount, quote.currency)}</span>
        </div>
      ))}
      <p className="font-bold">
        {t('total')} {formatMoney(quote.finalGross, quote.currency)}
      </p>
    </div>
  );
}
